
import React, { createContext, useContext, ReactNode } from 'react';
import { CURRENT_USER_ID, isUserRegistered, getUserEvents, getAvailableEvents, MasterEvent } from '@/utils/masterEventData';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface UserContextType {
  user: User | null;
  isUserRegisteredToEvent: (eventId: string | number) => boolean;
  getUserRegisteredEvents: () => MasterEvent[];
  getAvailableEventsForUser: () => MasterEvent[];
}

const UserContext = createContext<UserContextType>({ 
  user: null,
  isUserRegisteredToEvent: () => false,
  getUserRegisteredEvents: () => [],
  getAvailableEventsForUser: () => []
});

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Simulated user data - in a real app this would come from authentication/database
  const user: User = {
    id: CURRENT_USER_ID,
    name: "Juan Pérez González",
    email: "juan.perez@email.com",
    phone: "+34 600 123 456"
  };

  const isUserRegisteredToEvent = (eventId: string | number): boolean => {
    // Convert string UUID to number for legacy compatibility
    const numericId = typeof eventId === 'string' ? parseInt(eventId.slice(-8), 16) % 100 : eventId;
    return isUserRegistered(numericId, user.id);
  };

  const getUserRegisteredEvents = (): MasterEvent[] => {
    return getUserEvents(user.id);
  };

  const getAvailableEventsForUser = (): MasterEvent[] => {
    return getAvailableEvents(user.id);
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      isUserRegisteredToEvent,
      getUserRegisteredEvents,
      getAvailableEventsForUser
    }}>
      {children}
    </UserContext.Provider>
  );
};
